import React from 'react';
import { AlertCircle, Sparkles } from 'lucide-react';
import { GoogleIcon, MetaIcon } from './index';

const CTA_BUTTON_CLASS =
  'px-8 py-4 rounded-full font-bold text-white text-base sm:text-lg ' +
  'backdrop-blur-xl bg-gradient-to-r from-blue-500/25 via-indigo-500/25 to-blue-600/25 ' +
  'border border-blue-400/30 shadow-[0_4px_14px_-2px_rgba(59,130,246,0.35),0_4px_20px_-4px_rgba(99,102,241,0.4)] ' +
  'hover:border-blue-400/50 transition-all duration-300 active:scale-[0.98] flex items-center justify-center gap-2';

/**
 * Vista compartida del paso "definir objetivos": selector de conversiones + Generar con IA.
 * Se usa en Home (StepInfoPanel) y en la página Objetivos cuando el usuario no completó ese paso.
 */
const ObjectivesSetupView = ({
  googleConversions = [],
  metaConversions = [],
  selectedConversions = [],
  onToggleConversion,
  onSelectAllForPlatform,
  onGenerate,
  selectedObjectiveMetrics = [],
  onToggleObjectiveMetric,
  loading = false,
  isGoogleConnected = false,
  isMetaConnected = false,
  compact = false,
}) => {
  const singlePlatform = (googleConversions.length > 0 && metaConversions.length === 0) ||
    (googleConversions.length === 0 && metaConversions.length > 0);

  const allConversions = [...(googleConversions || []), ...(metaConversions || [])];
  const hasAnyConversionsListed = allConversions.length > 0;

  const ConversionTable = ({ title, icon: Icon, conversions, platform }) => {
    if (conversions.length === 0) return null;
    const ids = conversions.map((c) => c.id);
    const allSelected = ids.length > 0 && ids.every((id) => selectedConversions.includes(id));
    const sortedConversions = [...conversions].sort((a, b) => {
      const aSelected = selectedConversions.includes(a.id);
      const bSelected = selectedConversions.includes(b.id);

      // Keep selected ones at the top.
      if (aSelected && !bSelected) return -1;
      if (!aSelected && bSelected) return 1;

      // Then sort by volume in last 30 days (desc).
      const aMonthly = Number(a?.monthlyConversions ?? 0) || 0;
      const bMonthly = Number(b?.monthlyConversions ?? 0) || 0;
      if (bMonthly !== aMonthly) return bMonthly - aMonthly;

      // Stable-ish fallback.
      return String(a?.nombre ?? '').localeCompare(String(b?.nombre ?? ''), 'es', { sensitivity: 'base' });
    });
    return (
      <div className={`w-full rounded-xl bg-white/5 border border-white/10 overflow-hidden ${singlePlatform ? 'max-w-2xl' : 'max-w-xl'}`}>
        <div className="flex items-center justify-between px-4 py-2 bg-white/5 border-b border-white/10">
          <div className="flex items-center gap-2">
            <Icon width={20} height={20} />
            <span className="font-semibold text-white text-sm">{title}</span>
            <span className="text-white/50 text-xs">({conversions.length})</span>
          </div>
          <button
            type="button"
            onClick={() => onSelectAllForPlatform(platform)}
            className="min-h-[44px] min-w-[44px] shrink-0 rounded-md px-3 py-2 md:min-h-8 md:px-2 md:py-1.5 text-xs font-medium text-primary-light hover:text-white transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/60 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900"
          >
            {allSelected ? 'Desmarcar todas' : 'Seleccionar todas'}
          </button>
        </div>
        <div className="max-h-40 overflow-y-auto divide-y divide-white/5">
          {sortedConversions.map((conv) => (
            <label
              key={conv.id}
              className="flex min-h-11 items-center gap-3 px-4 py-2 hover:bg-white/5 cursor-pointer transition-colors focus-within:ring-2 focus-within:ring-inset focus-within:ring-primary/40"
            >
              <input
                type="checkbox"
                checked={selectedConversions.includes(conv.id)}
                onChange={() => onToggleConversion(conv.id)}
                className="h-4 w-4 shrink-0 rounded border-white/30 bg-white/10 text-primary focus:outline-none focus:ring-2 focus:ring-primary/60 focus:ring-offset-2 focus:ring-offset-slate-900/80"
              />
              <div className="flex-1 min-w-0">
                <span className="text-sm text-white/90 truncate block">{conv.nombre}</span>
                <span className="text-[11px] text-white/60">
                  Últimos 30 días: {Number(conv?.monthlyConversions ?? 0).toLocaleString('es-ES')}
                </span>
              </div>
            </label>
          ))}
        </div>
      </div>
    );
  };

  const objectiveMetricOptions = [
    { value: 'Presupuesto', label: 'Inversión' },
    { value: 'Conversiones', label: 'Conversiones' },
    { value: 'CPA', label: 'CPA' },
    { value: 'ROAS', label: 'ROAS' },
  ];
  const shouldShowConversionSelector = Array.isArray(selectedObjectiveMetrics) &&
    selectedObjectiveMetrics.some((metric) => ['Conversiones', 'CPA', 'ROAS'].includes(metric));

  const hasObjectiveSelection =
    Array.isArray(selectedObjectiveMetrics) && selectedObjectiveMetrics.length > 0;

  const requiresConversionPick =
    shouldShowConversionSelector && hasAnyConversionsListed;

  const generateDisabled =
    !hasObjectiveSelection ||
    (loading && shouldShowConversionSelector) ||
    (requiresConversionPick && selectedConversions.length === 0);

  const showConversionRequiredMessage =
    generateDisabled &&
    requiresConversionPick &&
    selectedConversions.length === 0 &&
    !loading;

  const showObjectiveRequiredMessage = !hasObjectiveSelection && !loading;

  const ctaDescribedBy = [
    showObjectiveRequiredMessage ? 'objectives-setup-metric-required' : null,
    showConversionRequiredMessage ? 'objectives-setup-conversion-required' : null,
  ]
    .filter(Boolean)
    .join(' ') || undefined;

  return (
    <div className={`relative flex flex-col items-center justify-center ${compact ? 'p-4 min-h-0' : 'p-8 sm:p-12 min-h-[320px]'}`}>
      {!compact && (
        <>
          <div className="w-full max-w-4xl relative mb-4 overflow-hidden">
            <h2 className="text-[2.1rem] sm:text-[2.6rem] md:text-[3.15rem] font-bold text-white text-center leading-tight [mask-image:linear-gradient(to_right,transparent_0%,black_12%,black_88%,transparent_100%)] [-webkit-mask-image:linear-gradient(to_right,transparent_0%,black_12%,black_88%,transparent_100%)]">
              Por último, es momento de definir los objetivos 🎯 para tus cuentas
            </h2>
          </div>

          <p className="text-white/80 text-base sm:text-lg text-center mb-6 max-w-2xl">
            Vamos a analizar tus cuentas para armar una proyección de resultados y, en base a esto,
            generarte{' '}
            <span className="px-2 py-0.5 rounded-lg bg-gradient-to-r from-cyan-500/30 via-blue-500/35 to-indigo-500/30 border border-cyan-400/50 text-cyan-100 font-semibold shadow-[0_0_12px_rgba(34,211,238,0.25)]">
              optimizaciones semanales
            </span>
            {' '}para alcanzar estos objetivos!
          </p>
        </>
      )}

      <div
        className={`w-full flex flex-col items-center mt-6 ${
          compact ? 'gap-6' : 'gap-8'
        }`}
      >
        <p className="text-white/90 text-base text-center">
          ¿Qué objetivos querés crear?
        </p>

        <div className={`w-full grid gap-3 ${compact ? 'grid-cols-2' : 'grid-cols-1 sm:grid-cols-2'}`}>
          {objectiveMetricOptions.map((opt) => {
            const checked = Array.isArray(selectedObjectiveMetrics) && selectedObjectiveMetrics.includes(opt.value);
            return (
              <label
                key={opt.value}
                className="flex min-h-[44px] md:min-h-0 items-start gap-3 px-4 py-3 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 hover:border-white/20 transition-all duration-200 cursor-pointer focus-within:ring-2 focus-within:ring-blue-400/50 focus-within:ring-offset-2 focus-within:ring-offset-slate-900"
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => onToggleObjectiveMetric?.(opt.value)}
                  className="mt-1 h-4 w-4 shrink-0 rounded border-white/30 bg-white/10 text-primary focus:outline-none focus:ring-2 focus:ring-primary/60 focus:ring-offset-2 focus:ring-offset-slate-900/80"
                />
                {opt.emoji ? <span className="text-lg shrink-0">{opt.emoji}</span> : null}
                <span className="flex-1 min-w-0">
                  <span className="block text-sm font-semibold text-white/90">{opt.label}</span>
                  <span className="block text-[11px] text-white/60">
                    {opt.value === 'Presupuesto'
                      ? 'Objetivos de inversión'
                      : opt.value === 'Conversiones'
                        ? 'Maximizá conversiones'
                        : opt.value === 'CPA'
                          ? 'Controlá el costo por adquisición'
                          : 'Buscá un retorno sobre inversión'}
                  </span>
                </span>
              </label>
            );
          })}
        </div>

        <p className="text-white/60 text-xs text-center max-w-md">
          Elegí al menos un tipo de objetivo. Si marcás Conversiones, CPA o ROAS y tu cuenta tiene
          conversiones configuradas, elegí al menos una.
        </p>

        {shouldShowConversionSelector && (
          <div className="w-full">
            {loading ? (
              <div
                className="flex flex-col items-center gap-3"
                role="status"
                aria-live="polite"
                aria-busy="true"
              >
                <div
                  className="w-8 h-8 border-2 border-cyan-400/50 border-t-cyan-300 rounded-full animate-spin"
                  aria-hidden
                />
                <p className="text-white/80 text-sm">Buscando conversiones en tus cuentas...</p>
              </div>
            ) : (
              <>
                {googleConversions.length === 0 && metaConversions.length === 0 && (isGoogleConnected || isMetaConnected) ? (
                  <p className="text-white/60 text-sm text-center">
                    No encontramos conversiones en tus cuentas. La IA generará objetivos genéricos.
                  </p>
                ) : hasAnyConversionsListed ? (
                  <>
                    <p className="text-white/90 text-base text-center mb-4">
                      Seleccioná tu <span className="font-bold text-white">conversión principal</span> para{' '}
                      <span className="font-bold text-white">generar objetivos y optimizaciones automáticas</span>.
                    </p>

                    <div
                      className={`w-full flex flex-col sm:flex-row gap-4 justify-center ${singlePlatform ? 'max-w-4xl' : 'max-w-2xl'} mx-auto`}
                    >
                      <ConversionTable
                        title="Google Ads"
                        icon={GoogleIcon}
                        conversions={googleConversions}
                        platform="GOOGLE"
                      />
                      <ConversionTable
                        title="Meta Ads"
                        icon={MetaIcon}
                        conversions={metaConversions}
                        platform="META"
                      />
                    </div>
                  </>
                ) : null}
              </>
            )}
          </div>
        )}

        {showObjectiveRequiredMessage ? (
          <p
            id="objectives-setup-metric-required"
            role="status"
            className="max-w-md text-center text-sm text-white/70"
          >
            Seleccioná al menos un tipo de objetivo para continuar.
          </p>
        ) : null}

        {showConversionRequiredMessage ? (
          <p
            id="objectives-setup-conversion-required"
            role="alert"
            className="max-w-md text-center text-sm text-error flex items-center justify-center gap-1.5"
          >
            <AlertCircle className="w-4 h-4 shrink-0" aria-hidden />
            Seleccioná al menos una conversión para generar con IA.
          </p>
        ) : null}

        <button
          type="button"
          onClick={onGenerate}
          disabled={generateDisabled}
          aria-busy={Boolean(loading && shouldShowConversionSelector)}
          aria-describedby={ctaDescribedBy}
          className={`${CTA_BUTTON_CLASS} min-h-[44px] min-w-[220px] md:min-h-8 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900 disabled:opacity-50 disabled:cursor-not-allowed`}
        >
          <Sparkles className="w-5 h-5 shrink-0" aria-hidden />
          Generar con IA
        </button>
      </div>
    </div>
  );
};

export default ObjectivesSetupView;
